import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import type { UnitSystem } from "@/lib/units";

interface UnitsContextValue {
  system: UnitSystem;
  setSystem: (s: UnitSystem) => void;
  toggle: () => void;
}

const UnitsContext = createContext<UnitsContextValue | undefined>(undefined);

const STORAGE_KEY = "ohmply.unitSystem";

const readInitial = (): UnitSystem => {
  if (typeof window === "undefined") return "imperial";
  const saved = window.localStorage.getItem(STORAGE_KEY);
  return saved === "metric" ? "metric" : "imperial";
};

export const UnitsProvider = ({ children }: { children: ReactNode }) => {
  const [system, setSystemState] = useState<UnitSystem>(readInitial);

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, system);
    } catch {
      /* ignore */
    }
  }, [system]);

  const setSystem = (s: UnitSystem) => setSystemState(s);
  const toggle = () => setSystemState((p) => (p === "imperial" ? "metric" : "imperial"));

  return (
    <UnitsContext.Provider value={{ system, setSystem, toggle }}>
      {children}
    </UnitsContext.Provider>
  );
};

export const useUnits = (): UnitsContextValue => {
  const ctx = useContext(UnitsContext);
  if (!ctx) throw new Error("useUnits must be used within UnitsProvider");
  return ctx;
};
